import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { usePlatformAuthStore } from './platformAuthStore';
import { platformAuthApi } from './api';
import { PlatformApiError } from './types';
import { Spinner } from '@/components/ui';

// Mirrors AppV2Routes.tsx's ProtectedRouteV2: hydrate tokens from storage on
// first mount, fetch the principal if we have a token but haven't loaded one
// yet, and redirect to /platform/login when there is no valid session.

function FullScreenSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f4f4f2]">
      <Spinner size="md" />
    </div>
  );
}

export function PlatformProtectedRoute({ children }: { children: React.ReactNode }) {
  const status = usePlatformAuthStore((s) => s.status);
  const accessToken = usePlatformAuthStore((s) => s.accessToken);
  const hydrateFromStorage = usePlatformAuthStore((s) => s.hydrateFromStorage);
  const setPrincipal = usePlatformAuthStore((s) => s.setPrincipal);

  useEffect(() => {
    if (status === 'idle') {
      hydrateFromStorage();
    }
  }, [status, hydrateFromStorage]);

  useEffect(() => {
    const principal = usePlatformAuthStore.getState().principal;
    if (status === 'authenticated' && accessToken && !principal) {
      platformAuthApi
        .me()
        .then((me) => setPrincipal({ id: me.id, full_name: me.full_name }))
        .catch((err: unknown) => {
          if (err instanceof PlatformApiError && err.status === 401) {
            usePlatformAuthStore.getState().logout();
          }
          // Network errors / 5xx: keep the session alive, let the operator retry.
        });
    }
  }, [status, accessToken, setPrincipal]);

  if (status === 'idle' || status === 'loading') {
    return <FullScreenSpinner />;
  }

  if (status === 'unauthenticated' || !accessToken) {
    return <Navigate to="/platform/login" replace />;
  }

  return <>{children}</>;
}
